import { useEffect, useState } from "react";

import { useGetApi, usePostApi } from "../../../../hooks";
import type { IFile } from "../../../../interfaces";
import { getYachtId } from "../../../../utils";
/**
 * Custom hook to fetch and manage attachments with URLs for a specific entry.
 *
 * @param lbeid - The ID of the entry for which attachments are being fetched.
 * @returns An array of `IFile` objects with URLs, or `null` if no attachments are available.
 */
export const useGetAttachments = (lbeid: string) => {
  const [attachmentsWithUrls, setAttachmentsWithUrls] = useState<IFile[]>([]);

  const { post } = usePostApi();
  const { data: attachments, refetch: fetchEntry } = useGetApi<{
    list: IFile[];
  }>({
    request: "get-entry-files",
    body: {
      params: {
        yachtid: getYachtId(),
        lbeid,
      },
    },
    fetchOnInit: false,
  });

  useEffect(() => {
    if (lbeid) void fetchEntry();
  }, [lbeid, fetchEntry]);

  const attachmentsList =
    attachments?.list && attachments.list.length > 0 ? attachments.list : null;

  const getAttachemntsUrls = async () => {
    if (attachmentsList) {
      const parsedAttachments: IFile[] = [];
      for (const attachment of attachmentsList) {
        const response = await post({
          request: "download-file-link",
          body: { params: { aid: attachment.aid } },
        });
        if (response?.link) {
          parsedAttachments.push({ ...attachment, url: response.link });
        }
      }
      setAttachmentsWithUrls(parsedAttachments);
    }
  };

  useEffect(() => {
    void getAttachemntsUrls();
  }, [attachmentsList]);

  return attachmentsWithUrls.length > 0 ? attachmentsWithUrls : null;
};
